import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ open_deals: 0, pipeline_value: 0, closed_won: 0, won_value: 0 });
  const [myDeals, setMyDeals] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get('/users').then(r => {
      setAllUsers(r.data.users || []);
      setSelectedUser(user.id);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const ownerId = selectedUser || user.id;
    setLoading(true);
    Promise.all([
      api.get('/deals/summary'),
      api.get(`/deals?owner_id=${ownerId}`),
      api.get(`/events?owner_id=${ownerId}`),
    ]).then(([s, d, e]) => {
      setSummary(s.data);

      // My deals — open only, sorted by amount
      const all = [];
      Object.values(d.data.deals || {}).forEach(arr => all.push(...arr));
      const open = all
        .filter(d => !['closed_won','closed_lost'].includes(d.stage))
        .sort((a, b) => parseFloat(b.amount || 0) - parseFloat(a.amount || 0));
      setMyDeals(open);

      const now = new Date();
      const events = e.data.events || [];

      // Upcoming — future open events
      const upcoming = events
        .filter(ev => new Date(ev.start_time) >= now && ev.status === 'open')
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
        .slice(0, 6);
      setUpcomingEvents(upcoming);

      // Recent activity — past events sorted newest first
      const recent = events
        .filter(ev => new Date(ev.start_time) < now || ev.status === 'closed')
        .sort((a, b) => new Date(b.start_time || b.created_at) - new Date(a.start_time || a.created_at))
        .slice(0, 8);
      setRecentActivity(recent);

    }).catch(() => {}).finally(() => setLoading(false));
  }, [user, selectedUser]);

  const fmt = n => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });

  const stageBadge = s => {
    const map = {
      prospecting: { cls:'badge-gray', label:'Prospecting' },
      qualified: { cls:'badge-blue', label:'Qualified' },
      proposal: { cls:'badge-amber', label:'Proposal' },
      negotiation: { cls:'badge-red', label:'Negotiation' },
      closed_won: { cls:'badge-green', label:'Closed won' },
      closed_lost: { cls:'badge-gray', label:'Closed lost' },
    };
    const b = map[s] || { cls:'badge-gray', label:s };
    return <span className={`badge ${b.cls}`}>{b.label}</span>;
  };

  const typeIcon = t => ({ meeting:'📅', call:'📞', virtual_call:'💻' }[t] || '📅');
  const typeColor = t => ({
    meeting: { bg:'#E6F1FB', text:'#0C447C' },
    call: { bg:'#EAF3DE', text:'#27500A' },
    virtual_call: { bg:'#FAEEDA', text:'#633806' },
  }[t] || { bg:'#F1EFE8', text:'#444441' });

  const formatEventTime = (start) => {
    const d = new Date(start);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const isToday = d.toDateString() === today.toDateString();
    const isTomorrow = d.toDateString() === tomorrow.toDateString();
    const time = d.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
    if (isToday) return `Today ${time}`;
    if (isTomorrow) return `Tomorrow ${time}`;
    return d.toLocaleDateString('en-US', { month:'short', day:'numeric' }) + ' ' + time;
  };

  const formatPastTime = (start) => {
    const d = new Date(start);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000 / 60);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
    if (diff < 10080) return `${Math.floor(diff/1440)}d ago`;
    return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading dashboard…</div>;

  return (
    <>
      {/* Header row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:600, color:'var(--text-primary)' }}>
            Good {greeting}, {user?.full_name?.split(' ')[0]} 👋
          </div>
          <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:4 }}>
            {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}
          </div>
        </div>
        {/* Rep selector for managers */}
        {user?.role === 'admin' && (
          <select
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none' }}
          >
            {allUsers.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Summary stats */}
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <div className="stat-card">
          <div className="stat-label">Open opportunities</div>
          <div className="stat-value">{summary.open_deals || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pipeline value</div>
          <div className="stat-value">{fmt(summary.pipeline_value)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Closed won</div>
          <div className="stat-value" style={{ color:'var(--success)' }}>{summary.closed_won || 0}</div>
          <div className="stat-change">{fmt(summary.won_value)} total</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">My open opportunities</div>
          <div className="stat-value">{myDeals.length}</div>
        </div>
      </div>

      {/* Top row — My opportunities + Upcoming events */}
      <div className="two-col" style={{ marginBottom:16 }}>

        {/* My opportunities */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div className="section-title" style={{ marginBottom:0 }}>My opportunities</div>
            <button className="btn" style={{ fontSize:12, height:28 }} onClick={() => navigate('/pipeline')}>
              View all
            </button>
          </div>
          {myDeals.length === 0
            ? <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'12px 0' }}>
                No open opportunities assigned to you yet.
              </div>
            : <table className="data-table">
                <thead>
                  <tr><th>Opportunity</th><th>Account</th><th>Stage</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {myDeals.slice(0, 6).map(d => (
                    <tr key={d.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/pipeline/${d.id}`)}>
                      <td style={{ fontWeight:500, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {d.name}
                      </td>
                      <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{d.company_name || '—'}</td>
                      <td>{stageBadge(d.stage)}</td>
                      <td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(d.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                {myDeals.length > 6 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ padding:'8px 12px', fontSize:12, color:'var(--text-tertiary)', textAlign:'center' }}>
                        +{myDeals.length - 6} more ·{' '}
                        <span style={{ color:'var(--accent-dark)', cursor:'pointer' }} onClick={() => navigate('/pipeline')}>
                          View all
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
          }
        </div>

        {/* Upcoming events */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div className="section-title" style={{ marginBottom:0 }}>Upcoming events</div>
            <button className="btn" style={{ fontSize:12, height:28 }} onClick={() => navigate('/calendar')}>
              View calendar
            </button>
          </div>
          {upcomingEvents.length === 0
            ? <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'12px 0' }}>
                No upcoming events.{' '}
                <span style={{ color:'var(--accent-dark)', cursor:'pointer' }} onClick={() => navigate('/calendar')}>
                  Add one on the calendar →
                </span>
              </div>
            : <div className="activity-list">
                {upcomingEvents.map(ev => {
                  const tc = typeColor(ev.type);
                  return (
                    <div key={ev.id} className="activity-item" style={{ cursor:'pointer' }}
                      onClick={() => navigate('/calendar')}>
                      <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                                    background: tc.bg, display:'flex', alignItems:'center',
                                    justifyContent:'center', fontSize:14 }}>
                        {typeIcon(ev.type)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {ev.title}
                        </div>
                        <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2, display:'flex', gap:6 }}>
                          <span>{formatEventTime(ev.start_time)}</span>
                          {ev.company_name && <><span>·</span><span>{ev.company_name}</span></>}
                          {ev.contact_name && <><span>·</span><span>{ev.contact_name}</span></>}
                        </div>
                      </div>
                      <span style={{ fontSize:10, padding:'2px 6px', borderRadius:20,
                                     background: tc.bg, color: tc.text, flexShrink:0, fontWeight:500 }}>
                        {ev.type.replace('_',' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      </div>

      {/* Bottom row — Recent activity feed */}
      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div className="section-title" style={{ marginBottom:0 }}>Recent activity</div>
          <button className="btn" style={{ fontSize:12, height:28 }} onClick={() => navigate('/calendar')}>
            View all
          </button>
        </div>
        {recentActivity.length === 0
          ? <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'8px 0' }}>
              No recent activity. Log calls and meetings from any account, contact, or opportunity.
            </div>
          : <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
              {recentActivity.map(ev => {
                const tc = typeColor(ev.type);
                return (
                  <div key={ev.id} style={{ display:'flex', gap:10, padding:'10px 0',
                                             borderBottom:'0.5px solid var(--border)', alignItems:'flex-start' }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                                  background: tc.bg, display:'flex', alignItems:'center',
                                  justifyContent:'center', fontSize:14 }}>
                      {typeIcon(ev.type)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {ev.title}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2, display:'flex', gap:6, flexWrap:'wrap' }}>
                        <span style={{ padding:'1px 6px', borderRadius:20, background: tc.bg, color: tc.text, fontWeight:500 }}>
                          {ev.type.replace('_',' ')}
                        </span>
                        {ev.company_name && <span>{ev.company_name}</span>}
                        {ev.contact_name && <><span>·</span><span>{ev.contact_name}</span></>}
                        <span style={{ marginLeft:'auto' }}>{formatPastTime(ev.start_time)}</span>
                      </div>
                      {ev.notes && (
                        <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:4,
                                       overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {ev.notes}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        }
      </div>
    </>
  );
}